// skipcq: JS-W1028, JS-0128
import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

import useBeerStyles from '../../hooks/useBeerStyles';
import useBeerStyleTypes from '../../hooks/useBeerStyleTypes';

function buildStyleOptions(beerStyles, styleTypeFilter) {
  const normalizedFilter =
    typeof styleTypeFilter === 'string' ? styleTypeFilter.trim() : '';

  return beerStyles
    .filter((styleRow) => {
      if (!normalizedFilter) {
        return true;
      }
      return (styleRow.type || '') === normalizedFilter;
    })
    .map((styleRow) => ({
      id: styleRow.id,
      name: styleRow.name || styleRow.id,
      type: styleRow.type || 'Uncategorized',
    }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

function resolveAutocompleteValue(catalogStyleId, styleLabel, styleOptions) {
  if (catalogStyleId) {
    const matchedOption = styleOptions.find((option) => option.id === catalogStyleId);
    if (matchedOption) {
      return matchedOption;
    }
  }
  if (typeof styleLabel === 'string' && styleLabel.trim()) {
    return styleLabel.trim();
  }
  return null;
}

function BeerStyleTypeFields({
  styleType,
  onStyleTypeChange,
  catalogStyleId,
  styleLabel,
  onStyleSelectionChange,
  disabled = false,
  required = false,
  styleTypeError,
  styleError,
}) {
  const { beerStyles, loading: stylesLoading } = useBeerStyles();
  const { styleTypes } = useBeerStyleTypes();

  const styleOptions = useMemo(
    () => buildStyleOptions(beerStyles, styleType),
    [beerStyles, styleType],
  );

  const autocompleteValue = useMemo(
    () => resolveAutocompleteValue(catalogStyleId, styleLabel, styleOptions),
    [catalogStyleId, styleLabel, styleOptions],
  );

  const handleStyleTypeSelectChange = useCallback(
    (event) => {
      const nextType = event.target.value;
      onStyleTypeChange(nextType);
      onStyleSelectionChange({
        styleType: nextType,
        catalogStyleId: '',
        styleLabel: '',
        isCustom: false,
      });
    },
    [onStyleSelectionChange, onStyleTypeChange],
  );

  const handleStyleAutocompleteChange = useCallback(
    (event, newValue) => {
      if (typeof newValue === 'string') {
        const trimmedName = newValue.trim();
        onStyleSelectionChange({
          styleType,
          catalogStyleId: '',
          styleLabel: trimmedName,
          isCustom: trimmedName.length > 0,
        });
        return;
      }

      if (newValue && typeof newValue === 'object' && newValue.id) {
        onStyleSelectionChange({
          styleType: newValue.type && newValue.type !== 'Uncategorized'
            ? newValue.type
            : styleType,
          catalogStyleId: newValue.id,
          styleLabel: newValue.name || newValue.id,
          isCustom: false,
        });
        if (newValue.type && newValue.type !== 'Uncategorized') {
          onStyleTypeChange(newValue.type);
        }
        return;
      }

      onStyleSelectionChange({
        styleType,
        catalogStyleId: '',
        styleLabel: '',
        isCustom: false,
      });
    },
    [onStyleSelectionChange, onStyleTypeChange, styleType],
  );

  const handleStyleInputChange = useCallback(
    (event, newInputValue, reason) => {
      if (reason === 'reset') {
        return;
      }
      if (reason === 'clear') {
        onStyleSelectionChange({
          styleType,
          catalogStyleId: '',
          styleLabel: '',
          isCustom: false,
        });
        return;
      }
      if (reason === 'input' || reason === 'blur') {
        const nextLabel = typeof newInputValue === 'string' ? newInputValue : '';
        const trimmedName = nextLabel.trim();
        onStyleSelectionChange({
          styleType,
          catalogStyleId: '',
          styleLabel: nextLabel,
          isCustom: trimmedName.length > 0,
        });
      }
    },
    [onStyleSelectionChange, styleType],
  );

  const getOptionLabel = useCallback((option) => {
    if (typeof option === 'string') {
      return option;
    }
    return option?.name || '';
  }, []);

  const renderStyleInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Beer style"
        placeholder="Select or enter a style"
        required={required}
        error={Boolean(styleError)}
        helperText={styleError}
        fullWidth
      />
    ),
    [required, styleError],
  );

  const isOptionEqualToValue = useCallback((option, value) => {
    if (typeof option === 'string' || typeof value === 'string') {
      return option === value;
    }
    return option?.id === value?.id;
  }, []);

  if (stylesLoading) {
    return (
      <>
        <FormControl fullWidth required={required} error={Boolean(styleTypeError)}>
          <InputLabel id="beer-style-type-label">Style type</InputLabel>
          <Select
            labelId="beer-style-type-label"
            value={styleType || ''}
            label="Style type"
            disabled
          >
            <MenuItem value="">Loading…</MenuItem>
          </Select>
          {styleTypeError ? <FormHelperText>{styleTypeError}</FormHelperText> : null}
        </FormControl>
        <TextField
          label="Beer style"
          required={required}
          error={Boolean(styleError)}
          helperText={styleError}
          fullWidth
          disabled
          InputProps={{
            endAdornment: <CircularProgress size={16} />,
          }}
        />
      </>
    );
  }

  return (
    <>
      <FormControl fullWidth required={required} error={Boolean(styleTypeError)}>
        <InputLabel id="beer-style-type-label">Style type</InputLabel>
        <Select
          labelId="beer-style-type-label"
          value={styleType || ''}
          label="Style type"
          onChange={handleStyleTypeSelectChange}
          disabled={disabled}
        >
          {styleTypes.map((typeRow) => (
            <MenuItem key={typeRow.id || typeRow.name} value={typeRow.name}>
              {typeRow.name}
            </MenuItem>
          ))}
        </Select>
        {styleTypeError ? <FormHelperText>{styleTypeError}</FormHelperText> : null}
      </FormControl>
      <Autocomplete
        freeSolo
        options={styleOptions}
        getOptionLabel={getOptionLabel}
        value={autocompleteValue}
        onChange={handleStyleAutocompleteChange}
        onInputChange={handleStyleInputChange}
        disabled={disabled}
        fullWidth
        renderInput={renderStyleInput}
        isOptionEqualToValue={isOptionEqualToValue}
      />
    </>
  );
}

BeerStyleTypeFields.propTypes = {
  styleType: PropTypes.string,
  onStyleTypeChange: PropTypes.func.isRequired,
  catalogStyleId: PropTypes.string,
  styleLabel: PropTypes.string,
  onStyleSelectionChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  styleTypeError: PropTypes.string,
  styleError: PropTypes.string,
};

BeerStyleTypeFields.defaultProps = {
  styleType: '',
  catalogStyleId: '',
  styleLabel: '',
  disabled: false,
  required: false,
  styleTypeError: null,
  styleError: null,
};

export default BeerStyleTypeFields;
